import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react"

import ConfirmationModal, {
  ModalInfo,
} from "components/Modals/ConfirmationModal"
import { Fee, FeeConfigScope } from "models/fees"
import { Table, createDataSource, useDatasource } from "components/Bable"
import { css, cx } from "@emotion/css"
import { deleteFee, fetchFeeSemantics, fetchFees } from "api/fees"
import { MutateOptions, useMutation, useQuery } from "react-query"

import { AddNewFee } from "./AddNewFee"
import { ApiError } from "api/http/utils"
import { Box } from "@material-ui/core"
import Button from "ui/buttons/Button"
import { EmptyState } from "components/EmptyState"
import { Loading } from "components/Loading"
import Surface from "components/Surface"
import { ReactComponent as TaxesIcon } from "assets/icon-tax.svg"
import { columns } from "./columns"
import { fetchMediaTypeSemantics } from "api/globalMediaTypes"
import { fetchSupplierProducts } from "api/suppliers"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useParams } from "react-router"
import { filterSemantics, mapBy } from "../Commissions/utils"

const getRowId = (x: Fee) => x.id

export const globalState = createDataSource({ getRowId })

type Props = {
  queryKeyParam: string
  configScope?: FeeConfigScope
}

const Fees = (props: Props) => {
  const { t } = useI18n()
  const { id, configId } = useParams<{ id: string; configId?: string }>()
  const { configScope, queryKeyParam } = props
  const [modalInfo, setModalInfo] = useState<ModalInfo>()
  const [open, setOpen] = useState<boolean>(false)
  const isModalOpen = Boolean(modalInfo)
  const { meta, hasAuthority } = useAuthenticated()
  const isScopeVendor = configScope === "VENDOR"
  const allowed = hasAuthority(["AUTH_VIEW_FEE_SUPPLIER"]) && isScopeVendor

  useEffect(() => globalState.setErrors({}), [])

  // ** fetch media type semantics
  const { data: mediaTypesSemantics, updatedAt: mediaSemanticsUpdatedAt } =
    useQuery([`${queryKeyParam}-media-type-semantics`], fetchMediaTypeSemantics)

  // ** fetch fees
  const { data: fees, isLoading } = useQuery(
    [`${queryKeyParam}-fees`, configScope, configId || id],
    fetchFees,
    {
      onSuccess: (d: Fee[]) => globalState.setData(d),
      enabled: !!mediaTypesSemantics,
    },
  )

  // ** fetch semantics
  const { data: feesSemantics } = useQuery(
    [
      "fees-semantics",
      (!isScopeVendor ? "AGENCY" : "VENDOR") as Partial<FeeConfigScope>,
    ],
    fetchFeeSemantics,
  )

  // ** fetch vendor products
  const { data: vendorProducts, updatedAt: vpUpdatedAt } = useQuery(
    ["market-vendors-products", id],
    fetchSupplierProducts,
    {
      enabled: allowed,
    },
  )

  // ** remove fee
  const [remove] = useMutation(deleteFee, {
    onSuccess: (_, id: string) => {
      setModalInfo(undefined)
      globalState.removeRow(id)
    },
    onError: () => setModalInfo(undefined),
  })

  const source = useDatasource({
    state: globalState,
  })

  const onUpdate = async (
    row: Fee,
    patchAction: (
      variables: Partial<Fee>,
      options?: MutateOptions<Fee, Partial<Fee>, ApiError, unknown> | undefined,
    ) => Promise<Fee>,
    label: string,
    value?: string | number | string[],
  ) => {
    const upRow = { ...row } as Fee
    patchAction({ ...upRow, [label]: value })
  }

  const handleDelete = useCallback(
    (row: Fee) => {
      setModalInfo({
        title: t("admin.fees.delete.title"),
        type: "primary_destructive",
        description: t("admin.fees.delete.description"),
        submitLabel: t("admin.fees.delete.action"),
        onSubmit: async () => await remove(row?.id),
      })
    },
    [remove, t],
  )

  const handleClick = () => setOpen(true)

  const onClose = () => setOpen(false)

  const mediaSemanticOptions = useMemo(
    () =>
      mediaTypesSemantics?.map(mapBy("name", "code", (v) => v.toUpperCase())) ||
      [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mediaSemanticsUpdatedAt],
  )

  const vendorProductOptions = useMemo(
    () =>
      vendorProducts
        ?.map(mapBy("name", "id", undefined, "mediaTypeSemanticCode"))
        ?.sort((a, b) => ((a.parent || "") > (b.parent || "") ? 1 : -1)) || [],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vpUpdatedAt],
  )

  const vendorProductsMapped = (row?: Fee) =>
    filterSemantics("parent", vendorProductOptions, row) || []

  const feesSemanticsOptions =
    feesSemantics?.map((o) => ({ label: o.name, value: o.value })) || []

  const hasMediaTypes = (row?: Fee) => {
    const upRow = { ...row }
    return (
      !!upRow && !!upRow.mediaTypeSemantics && !!upRow.mediaTypeSemantics.length
    )
  }

  const context = {
    mediaSemanticOptions,
    feesSemanticsOptions,
    vendorProductsOptions: vendorProductsMapped,
    onUpdate,
    hasMediaTypes,
    onDelete: handleDelete,
  }

  useEffect(() => {
    return () => {
      globalState.setData([])
    }
  }, [])

  // TODO: We should create an API with proper authorities, not handling the authorization of Agency Finance to the Front

  const isAFBlocked = isScopeVendor && meta?.userRole === "AGENCY_FINANCE"

  const disableAction =
    isAFBlocked ||
    !hasAuthority([
      "AUTH_CREATE_FEE_CLIENT",
      "AUTH_CREATE_FEE_AGENCY",
      "AUTH_CREATE_FEE_SUPPLIER",
    ])

  const disableEdit =
    isAFBlocked ||
    !hasAuthority([
      "AUTH_EDIT_FEE_AGENCY",
      "AUTH_EDIT_FEE_CLIENT",
      "AUTH_EDIT_FEE_SUPPLIER",
    ])

  return (
    <contextOptions.Provider value={context}>
      <Surface size="ghost">
        {open ? (
          <AddNewFee
            onClose={onClose}
            isLoading={false}
            data={fees}
            configOwnerId={configId || id}
            {...props}
          />
        ) : (
          <Box display="flex" justifyContent="flex-end">
            {!disableAction && (
              <Button
                variant="contained"
                color="primary"
                onClick={handleClick}
                disabled={open}
                style={{
                  marginBottom: "var(--spacing-xxl-2)",
                }}
              >
                {isScopeVendor
                  ? t("admin.cost.create_button")
                  : t("admin.fee.create_button")}
              </Button>
            )}
          </Box>
        )}
        {isLoading ? (
          <Loading />
        ) : (
          <Table<Fee>
            columns={columns(
              allowed,
              disableAction,
              disableEdit,
              isScopeVendor,
            )}
            source={source}
            height={650}
            className={styles.table}
            pending={isLoading}
            emptyText={
              <EmptyState
                title={
                  isScopeVendor
                    ? t("media.costs.noresults.title")
                    : t("media.fees.noresults.title")
                }
                icon={<TaxesIcon />}
                subtitle={
                  !disableAction
                    ? t("media.fields.noresults.subtitle")
                    : undefined
                }
              >
                {!disableAction ? (
                  <span
                    onClick={handleClick}
                    className={cx(
                      "BodyBody-1Primary-mainRegular",
                      styles.createNew,
                    )}
                  >
                    {isScopeVendor
                      ? t("media.costs.add_new_subtitle")
                      : t("media.fields.add_new_subtitle")}
                  </span>
                ) : null}
              </EmptyState>
            }
            width="initial"
          />
        )}
        <ConfirmationModal
          open={isModalOpen}
          onCancel={() => setModalInfo(undefined)}
          {...modalInfo}
        />
      </Surface>
    </contextOptions.Provider>
  )
}

export default Fees

const contextOptions = createContext<
  | {
      mediaSemanticOptions: {
        name: string
        value: string
      }[]
      feesSemanticsOptions: {
        label: string
        value: string
      }[]
      vendorProductsOptions: (row?: Fee) => {
        name: string
        value: string
      }[]
      onUpdate: (
        row: Fee,
        patchAction: (
          variables: Partial<Fee>,
          options?:
            | MutateOptions<Fee, Partial<Fee>, ApiError, unknown>
            | undefined,
        ) => Promise<Fee>,
        label: string,
        value?: string | number | string[] | undefined,
      ) => Promise<void>
      hasMediaTypes: (row?: Fee) => boolean
      onDelete: (row: Fee) => void
    }
  | undefined
>(undefined)

export const useContextOptions = () => {
  const options = useContext(contextOptions)

  if (options === undefined) {
    throw new Error("error")
  }
  return options
}

const styles = {
  tableToolbar: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: var(--spacing-m) 0 var(--spacing-xxl-2);

    .left {
      display: flex;
      align-items: center;
      & > :not(:first-child) {
        margin-left: var(--spacing-m);
      }
    }
  `,
  table: css`
    .RebaseTable__header-cell {
      padding: 0 12px;
      font-size: 12px;
      color: var(--black);
      font-weight: bold;
      text-transform: capitalize;
    }
    .RebaseTable__grid-cell {
    }

    .RebaseTable__body-row:hover {
      background-color: var(--primary-light);
    }

    .RebaseTable__body-row--hovered {
      background-color: var(--primary-light);
    }

    .RebaseTable__cell {
      &[data-column-id^="__"] {
        border-right: none;
        padding: 0;
      }
      :last-child {
        display: flex;
        justify-content: center;
      }
    }
    .RebaseTable__cell:focus-within {
      box-shadow: none;
    }
  `,
  createNew: css`
    padding-top: 1px;
    margin: 0 0.8ch;
    cursor: pointer;
  `,
}
